'use client';

import { useCallback, useState } from 'react';
import {
  type PerspectiveCategory,
  usePerspectiveTabContext,
} from '../perspectiveTabContext';

// The three perspectives the selectors expose, in display order.
export const PERSPECTIVE_ORDER: PerspectiveCategory[] = [
  'mainScenario',
  'public',
  'insider',
];

type PerspectiveSelection = {
  selected: PerspectiveCategory;
  // Set the page-level perspective. Every :::perspectiveBox re-renders to the
  // new tab on the next render — no animation, no scroll-anchoring.
  setPerspective: (category: PerspectiveCategory) => void;
};

// Shared read/write of the active perspective. Falls back to local state if
// no provider is mounted (e.g. a perspective box used outside the playbook)
// so standalone usage doesn't crash.
export const usePerspectiveSelection = (): PerspectiveSelection => {
  const context = usePerspectiveTabContext();
  const [localSelected, setLocalSelected] =
    useState<PerspectiveCategory>('mainScenario');

  const rawSelected = context
    ? context.selectedPerspective
    : localSelected;
  const setSelected = context
    ? context.setSelectedPerspective
    : setLocalSelected;

  // Null never maps to a selector button - show Main Scenario.
  const selected: PerspectiveCategory = rawSelected ?? 'mainScenario';

  const setPerspective = useCallback(
    (category: PerspectiveCategory) => {
      setSelected(category);
    },
    [setSelected],
  );

  return { selected, setPerspective };
};
